import { Toaster } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/toaster";
import { Toaster as Sonner } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/sonner";
import { TooltipProvider } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import Welcome from "./pages/Welcome";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import FitbitConnect from "./pages/FitbitConnect";
import Camera from "./pages/Camera";
import BarcodeScanner from "./pages/BarcodeScanner";
import BarcodeResult from "./pages/BarcodeResult";
import Home from "./pages/Home";
import Meals from "./pages/Meals";
import Fitness from "./pages/Fitness";
import Profile from "./pages/Profile";
import BottomNav from "./components/BottomNav";
import NotFound from "./pages/NotFound";
import LandingPage from "./pages/LandingPage";
import StepsDetail from "./pages/fitness/StepsDetail";
import HeartRateDetail from "./pages/fitness/HeartRateDetail";
import CaloriesBurnedDetail from "./pages/fitness/CaloriesBurnedDetail";
import ActiveMinutesDetail from "./pages/fitness/ActiveMinutesDetail";

import SleepDetail from "./pages/fitness/SleepDetail";
import HRVDetail from "./pages/fitness/HRVDetail";
import SpO2Detail from "./pages/fitness/SpO2Detail";
import BreathingRateDetail from "./pages/fitness/BreathingRateDetail";
import SkinTempDetail from "./pages/fitness/SkinTempDetail";
import StressScoreDetail from "./pages/fitness/StressScoreDetail";
import Fitness2 from "./pages/Fitness2";
import StressInputs from "./pages/fitness2/StressInputs";
import HRVDeviation from "./pages/fitness2/HRVDeviation";
import SleepRecovery from "./pages/fitness2/SleepRecovery";
import RestingHRDelta from "./pages/fitness2/RestingHRDelta";
import ActivityRecovery from "./pages/fitness2/ActivityRecovery";
import ImproveAccuracy from "./pages/fitness2/ImproveAccuracy";
import MealDetail from "./pages/meals/MealDetail";
import MealScore from "./pages/meals/MealScore";
import MealHistory from "./pages/meals/MealHistory";

import MealPrediction from "./pages/meals/MealPrediction";
import AddFoodItem from "./pages/meals/AddFoodItem";
import EditFoodItem from "./pages/meals/EditFoodItem";
import EditProfile from "./pages/profile/EditProfile";
import Goals from "./pages/profile/Goals";
import ConnectedDevices from "./pages/profile/ConnectedDevices";
import PersonalInformation from "./pages/profile/PersonalInformation";
import NotificationSettings from "./pages/profile/NotificationSettings";
import Preferences from "./pages/profile/Preferences";
import MedicationManagement from "./pages/profile/MedicationManagement";
import HelpSupport from "./pages/profile/HelpSupport";
import AboutBiteBalance from "./pages/profile/AboutBiteBalance";
import TermsOfService from "./pages/profile/TermsOfService";
import PrivacyPolicy from "./pages/profile/PrivacyPolicy";
import Licenses from "./pages/profile/Licenses";
import ViewProfilePhoto from "./pages/profile/ViewProfilePhoto";
import UploadProfilePhoto from "./pages/profile/UploadProfilePhoto";

import EmotionsStress from "./pages/home/EmotionsStress";
import LogActivity from "./pages/home/LogActivity";
import ActivityTrends from "./pages/home/ActivityTrends";
import ExerciseVideos from "./pages/home/ExerciseVideos";
import SuggestedWorkouts from "./pages/home/SuggestedWorkouts";
import FoodScoreDetail from "./pages/home/FoodScoreDetail";
import LifestyleScoreDetail from "./pages/home/LifestyleScoreDetail";
import PRALScoreDetail from "./pages/home/PRALScoreDetail";
import CarbFiberRatioDetail from "./pages/home/CarbFiberRatioDetail";
import FatQualityDetail from "./pages/home/FatQualityDetail";
import MicroDiversityDetail from "./pages/home/MicroDiversityDetail";
import GutRestDetail from "./pages/home/GutRestDetail";
import BiteBalanceDetail from "./pages/home/BiteBalanceDetail";
import RecoveryHRVDetail from "./pages/home/RecoveryHRVDetail";
import ProteinFiberRatioDetail from "./pages/home/ProteinFiberRatioDetail";
import RecoveryScoreDetail from "./pages/home/RecoveryScoreDetail";
import SugarLoadDetail from "./pages/home/SugarLoadDetail";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMeals from "./pages/admin/AdminMeals";
import AdminFitness from "./pages/admin/AdminFitness";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminBiteBalance from "./pages/admin/AdminBiteBalance";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminLogin from "./pages/admin/AdminLogin";
import { applyBiteBalancePageMeta } from "./lib/pageMeta";

const queryClient = new QueryClient();

const ADMIN_PAGE_TITLES: Record<string, string> = {
  "/admin": "Dashboard",
  "/admin/users": "Users",
  "/admin/meals": "Meals & Glucose",
  "/admin/fitness": "Fitness & Health",
  "/admin/bite-balance": "Bite Balance",
  "/admin/settings": "Settings",
  "/admin/login": "Login",
};

const DocumentTitle = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const adminBase = pathname.replace(/\/users\/[^/]+$/, "/users");
    const pageTitle = ADMIN_PAGE_TITLES[adminBase];
    applyBiteBalancePageMeta(pageTitle);
  }, [pathname]);

  return null;
};

const AppLayout = ({ children }: { children: React.ReactNode }) => (
  <>
    {children}
    <BottomNav />
  </>
);

type AppProps = {
  basename?: string;
};

const App = ({ basename = "/" }: AppProps) => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter basename={basename}>
          <DocumentTitle />
          <Routes>
            <Route path="/" element={<Navigate to="/admin/login" replace />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/fitbit-connect" element={<FitbitConnect />} />
            <Route path="/camera" element={<Camera />} />
            <Route path="/barcode" element={<BarcodeScanner />} />
            <Route path="/barcode/result" element={<BarcodeResult />} />
            <Route
              path="/home"
              element={
                <AppLayout>
                  <Home />
                </AppLayout>
              }
            />
            <Route
              path="/meals"
              element={
                <AppLayout>
                  <Meals />
                </AppLayout>
              }
            />
            <Route path="/meals/:mealType" element={<MealDetail />} />
            <Route path="/meals/:mealType/score" element={<MealScore />} />
            <Route path="/meals/history" element={<MealHistory />} />

            <Route path="/meals/prediction" element={<MealPrediction />} />
            <Route path="/meals/add-food-item" element={<AddFoodItem />} />
            <Route path="/meals/edit-food-item" element={<EditFoodItem />} />
            <Route
              path="/fitness"
              element={
                <AppLayout>
                  <Fitness />
                </AppLayout>
              }
            />
            <Route path="/fitness2" element={<Fitness2 />} />
            <Route path="/fitness2/inputs" element={<StressInputs />} />
            <Route path="/fitness2/hrv-deviation" element={<HRVDeviation />} />
            <Route
              path="/fitness2/sleep-recovery"
              element={<SleepRecovery />}
            />
            <Route path="/fitness2/resting-hr" element={<RestingHRDelta />} />
            <Route
              path="/fitness2/activity-recovery"
              element={<ActivityRecovery />}
            />
            <Route
              path="/fitness2/improve-accuracy"
              element={<ImproveAccuracy />}
            />
            <Route path="/fitness/steps" element={<StepsDetail />} />
            <Route path="/fitness/heart-rate" element={<HeartRateDetail />} />
            <Route
              path="/fitness/calories"
              element={<CaloriesBurnedDetail />}
            />
            <Route
              path="/fitness/active-minutes"
              element={<ActiveMinutesDetail />}
            />

            <Route path="/fitness/sleep" element={<SleepDetail />} />
            <Route path="/fitness/hrv" element={<HRVDetail />} />
            <Route path="/fitness/spo2" element={<SpO2Detail />} />
            <Route
              path="/fitness/breathing-rate"
              element={<BreathingRateDetail />}
            />
            <Route path="/fitness/skin-temp" element={<SkinTempDetail />} />
            <Route
              path="/fitness/stress-score"
              element={<StressScoreDetail />}
            />
            <Route path="/medications" element={<MedicationManagement />} />

            <Route path="/emotions-stress" element={<EmotionsStress />} />
            <Route path="/log-activity" element={<LogActivity />} />
            <Route path="/activity-trends" element={<ActivityTrends />} />
            <Route path="/exercise-videos" element={<ExerciseVideos />} />
            <Route path="/suggested-workouts" element={<SuggestedWorkouts />} />
            <Route path="/insights/gut-health" element={<FoodScoreDetail />} />
            <Route
              path="/insights/lifestyle-synergy"
              element={<LifestyleScoreDetail />}
            />
            <Route path="/insights/pral-score" element={<PRALScoreDetail />} />
            <Route
              path="/insights/carb-fiber"
              element={<CarbFiberRatioDetail />}
            />
            <Route
              path="/insights/fat-quality"
              element={<FatQualityDetail />}
            />
            <Route
              path="/insights/micro-diversity"
              element={<MicroDiversityDetail />}
            />
            <Route path="/insights/gut-rest" element={<GutRestDetail />} />
            <Route
              path="/insights/recovery-hrv"
              element={<RecoveryHRVDetail />}
            />
            <Route
              path="/insights/protein-fiber-ratio"
              element={<ProteinFiberRatioDetail />}
            />
            <Route
              path="/insights/recovery-score"
              element={<RecoveryScoreDetail />}
            />
            <Route
              path="/insights/bite-balance"
              element={<BiteBalanceDetail />}
            />
            <Route path="/insights/sugar-load" element={<SugarLoadDetail />} />
            <Route
              path="/profile"
              element={
                <AppLayout>
                  <Profile />
                </AppLayout>
              }
            />
            <Route path="/profile/edit" element={<EditProfile />} />
            <Route path="/profile/photo" element={<ViewProfilePhoto />} />
            <Route
              path="/profile/photo/upload"
              element={<UploadProfilePhoto />}
            />
            <Route
              path="/profile/personal-info"
              element={<PersonalInformation />}
            />
            <Route path="/profile/devices" element={<ConnectedDevices />} />
            <Route path="/profile/goals" element={<Goals />} />
            <Route
              path="/profile/notifications"
              element={<NotificationSettings />}
            />
            <Route path="/profile/preferences" element={<Preferences />} />
            <Route path="/profile/help" element={<HelpSupport />} />
            <Route path="/profile/about" element={<AboutBiteBalance />} />
            <Route path="/profile/terms" element={<TermsOfService />} />
            <Route path="/profile/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/profile/licenses" element={<Licenses />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:id" element={<AdminUserDetail />} />
              <Route path="meals" element={<AdminMeals />} />
              <Route path="fitness" element={<AdminFitness />} />
              <Route path="bite-balance" element={<AdminBiteBalance />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
